import { createReducer, on } from '@ngrx/store';
import { UserPreferencesActions } from './user-preferences.actions';
import { UserPreferencesState, initialUserPreferencesState } from './models/user-preferences-state.models';

export const userPreferencesReducer = createReducer(
  initialUserPreferencesState,

  // Load preferences
  on(UserPreferencesActions.loadPreferences, (state): UserPreferencesState => ({
    ...state,
    loading: true,
    error: null
  })),
  on(UserPreferencesActions.loadPreferencesSuccess, (state, { preferences }): UserPreferencesState => ({
    ...state,
    preferences,
    loading: false,
    error: null,
    cacheTimestamp: Date.now()
  })),
  on(UserPreferencesActions.loadPreferencesFailure, (state, { error }): UserPreferencesState => ({
    ...state,
    loading: false,
    error
  })),

  // Update preferences
  // Optimistically update active_avatar_id immediately for instant UI feedback
  // Other fields wait for server confirmation
  on(UserPreferencesActions.updatePreferences, (state, { data }): UserPreferencesState => {
    // Apply optimistic update for active_avatar_id if it's being changed
    // This eliminates the race condition when selecting avatars
    let optimisticPreferences = state.preferences;
    if (state.preferences && 'active_avatar_id' in data && data.active_avatar_id !== undefined) {
      optimisticPreferences = { 
        ...state.preferences, 
        active_avatar_id: data.active_avatar_id 
      };
    }

    return {
      ...state,
      preferences: optimisticPreferences,
      loading: true,
      error: null
    };
  }),
  on(UserPreferencesActions.updatePreferencesSuccess, (state, { preferences }): UserPreferencesState => ({
    ...state,
    preferences,
    loading: false,
    error: null,
    cacheTimestamp: Date.now()
  })),
  on(UserPreferencesActions.updatePreferencesFailure, (state, { error }): UserPreferencesState => ({
    ...state,
    loading: false,
    error
  })),

  // Update onboarding state
  on(UserPreferencesActions.updateOnboardingState, (state): UserPreferencesState => ({
    ...state,
    loading: true,
    error: null
  })),
  on(UserPreferencesActions.updateOnboardingStateSuccess, (state, { preferences }): UserPreferencesState => ({
    ...state,
    preferences,
    loading: false,
    error: null,
    cacheTimestamp: Date.now()
  })),
  on(UserPreferencesActions.updateOnboardingStateFailure, (state, { error }): UserPreferencesState => ({
    ...state,
    loading: false,
    error
  })),

  // Update UI preferences
  on(UserPreferencesActions.updateUiPreferences, (state): UserPreferencesState => ({
    ...state,
    loading: true,
    error: null
  })),
  on(UserPreferencesActions.updateUiPreferencesSuccess, (state, { preferences }): UserPreferencesState => ({
    ...state,
    preferences,
    loading: false,
    error: null,
    cacheTimestamp: Date.now()
  })),
  on(UserPreferencesActions.updateUiPreferencesFailure, (state, { error }): UserPreferencesState => ({
    ...state,
    loading: false,
    error
  })),

  // Set preferences directly
  on(UserPreferencesActions.setPreferences, (state, { preferences }): UserPreferencesState => ({
    ...state,
    preferences,
    loading: false,
    error: null,
    cacheTimestamp: Date.now()
  })),

  // Clear preferences
  on(UserPreferencesActions.clearPreferences, (): UserPreferencesState => ({
    ...initialUserPreferencesState
  })),

  // Invalidate cache
  on(UserPreferencesActions.invalidateCache, (state): UserPreferencesState => ({
    ...state,
    cacheTimestamp: null
  }))
);
